import { Buffer } from "buffer";
import type { Connection } from "@solana/web3.js";
import {
  PYTH_RECEIVER_PROGRAM_ID,
  PYTH_SOL_USD_PRICE_ACCOUNT,
} from "./constants";

const PRICE_UPDATE_V2_DISCRIMINATOR = Buffer.from("22f123639d7ef4cd", "hex");
const SOL_USD_FEED_ID = Buffer.from(
  "ef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d",
  "hex",
);
const MIN_PRICE_ACCOUNT_SIZE = 101;
const MAX_PRICE_AGE_SECONDS = 5 * 60;

export function parseSolUsdPrice(
  data: Buffer,
  nowSeconds = Math.floor(Date.now() / 1_000),
): number {
  if (data.length < MIN_PRICE_ACCOUNT_SIZE) {
    throw new Error("The SOL/USD oracle account is incomplete");
  }
  if (!Buffer.from(data.subarray(0, 8)).equals(PRICE_UPDATE_V2_DISCRIMINATOR)) {
    throw new Error("The SOL/USD oracle account has an unknown format");
  }
  if (data[40] !== 1) {
    throw new Error("The SOL/USD oracle price is not fully verified");
  }
  if (!Buffer.from(data.subarray(41, 73)).equals(SOL_USD_FEED_ID)) {
    throw new Error("The oracle account does not contain the SOL/USD feed");
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const rawPrice = Number(view.getBigInt64(73, true));
  const exponent = view.getInt32(89, true);
  const publishTime = Number(view.getBigInt64(93, true));
  const price = rawPrice * 10 ** exponent;
  const age = nowSeconds - publishTime;

  if (!Number.isFinite(price) || price <= 0) {
    throw new Error("The SOL/USD oracle returned an invalid price");
  }
  if (!Number.isSafeInteger(publishTime) || age < -60 || age > MAX_PRICE_AGE_SECONDS) {
    throw new Error("The SOL/USD oracle price is stale");
  }

  return price;
}

export async function loadSolUsdPrice(connection: Connection): Promise<number> {
  const account = await connection.getAccountInfo(
    PYTH_SOL_USD_PRICE_ACCOUNT,
    "confirmed",
  );
  if (!account) throw new Error("The SOL/USD oracle account was not found");
  if (!account.owner.equals(PYTH_RECEIVER_PROGRAM_ID)) {
    throw new Error("The SOL/USD oracle account has an unexpected owner");
  }
  return parseSolUsdPrice(account.data);
}
